// The driver record: every requirement grouped by owner, driver first.
//
// One container per owner rather than a flat list under headings — a vehicle's
// identity (plate, VIN, colour, seats) is context you need while deciding its
// documents, and a heading cannot hold it. Mirrors how the hub cards the same
// data for the driver.
import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '../../ui/core';
import { hairline, softFill } from '../../ui/styles';
import { CarIcon, PersonIcon, ChevronRightIcon, OpenInNewIcon, DeleteIcon, WarningIcon } from '../../ui/icons';
import {
  StatusDot,
  ItemStatusChip,
  RemedyLine,
  WaitingTag,
  Field,
  SectionLabel,
  SectionCard,
  SegmentedControl,
  SegmentedAction,
} from './components';
import { REMEDIES } from './model';

const OwnerIcon = ({ kind, ...props }) => (kind === 'vehicle' ? <CarIcon {...props} /> : <PersonIcon {...props} />);

const Row = ({ item, ownerTitle, onClick, imperative, external }) => {
  const actionable = Boolean(item.remedy);
  return (
    <Stack
      // The background check row opens Checkr rather than a local inspector: the
      // report, its history and its adjudication all live there, so anything here
      // could only paraphrase it.
      {...(external ? { component: 'a', href: external, target: '_blank', rel: 'noopener noreferrer' } : {})}
      direction="row"
      alignItems="flex-start"
      spacing={1.25}
      onClick={external ? undefined : onClick}
      sx={{
        px: 1.75,
        py: 1.1,
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'inherit',
        borderTop: (t) => `1px solid ${hairline(t)}`,
        '&:hover': { backgroundColor: (t) => softFill(t) },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* The dot belongs INSIDE the title's center-aligned row. As a sibling of
            this two-line block it aligns to the block's top edge, not to the
            title, which is what makes it read high. */}
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <StatusDot value={item.status} />
          <Typography sx={{ fontSize: '13.5px', fontWeight: 600 }}>
            {imperative && actionable ? `${REMEDIES[item.remedy].verb} ${item.label.toLowerCase()}` : item.label}
          </Typography>
          <ItemStatusChip value={item.status} />
          {/* Whose turn it is. A status says what state the document is in; only
              this says whether anyone is waiting on us for it. */}
          <WaitingTag waitingOn={item.waitingOn} />
        </Stack>
        {/* Indented to the title's text edge, past the dot */}
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.25, pl: '27px' }}>
          {imperative ? `${ownerTitle} · ` : ''}
          {item.why || item.detail}
        </Typography>
        {actionable && !imperative && (
          <Box sx={{ mt: 0.5, pl: '27px' }}>
            <RemedyLine remedy={item.remedy} />
          </Box>
        )}
      </Box>
      {/* An outward arrow where the row leaves the app, so it is obvious before
          clicking that this one is not a detail view. */}
      {external ? (
        <OpenInNewIcon sx={{ fontSize: 15, color: 'text.disabled', mt: 0.4 }} />
      ) : (
        <ChevronRightIcon sx={{ fontSize: 17, color: 'text.disabled', mt: 0.25 }} />
      )}
    </Stack>
  );
};

/**
 * Delete a vehicle, behind a confirm.
 *
 * Confirmed rather than undoable because it is not: the vehicle's documents go with
 * it, and the driver would have to re-upload them. The dialog therefore names what
 * goes, not just what is being clicked.
 *
 * Only on vehicles. There is no equivalent for the driver card — deleting a person
 * is deactivation, which the standing control above already owns.
 */
const DeleteVehicle = ({ vehicle, busy, onConfirm }) => {
  const [asking, setAsking] = useState(false);
  const documents = vehicle.documents?.length || 0;

  return (
    <>
      <IconButton
        size="small"
        disabled={busy}
        aria-label={`Delete ${vehicle.label || 'this vehicle'}`}
        onClick={() => setAsking(true)}
        sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
      >
        <DeleteIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Dialog open={asking} onClose={() => setAsking(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>Delete this vehicle?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
            {vehicle.label} is removed from the driver’s record
            {documents ? `, along with ${documents === 1 ? 'its document' : `its ${documents} documents`}` : ''}
            {vehicle.isDefault ? ', and another vehicle becomes their default' : ''}. They would have to add it again
            themselves.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAsking(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy}
            onClick={() => {
              setAsking(false);
              onConfirm(vehicle.driverVehicleId);
            }}
            sx={{ textTransform: 'none' }}
          >
            Delete vehicle
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * The one thing on a vehicle card that no document can fix.
 *
 * `is_eligible` is a fact about a make/model/year and it is shared: retiring a
 * model both hides it from the driver-facing add-vehicle pickers and benches every
 * driver already driving one. There is no way to release the second without undoing
 * the first, which is what `isGrandfathered` exists for — an exception on this
 * driver's row, leaving the catalog alone.
 *
 * Rendered ONLY on a retired model, or one already carrying an exception. On an
 * eligible vehicle the control would be a switch that does nothing today and might
 * matter years later, on every card, for an operation most reviewers will never
 * perform. If a model is retired later, this appears then.
 *
 * Says what is happening in the driver's app, not what the column is called: a
 * reviewer looking at a card whose every document reads Valid needs to know why the
 * driver still sees no trips, and "not eligible" alone does not say that.
 */
const VehicleEligibility = ({ vehicle, busy, onChange }) => {
  const [asking, setAsking] = useState(false);
  const granted = Boolean(vehicle.isGrandfathered);

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.75, pb: 1.25, flexWrap: 'wrap', rowGap: 0.5 }}>
        <WarningIcon sx={{ fontSize: 15, flexShrink: 0, color: granted ? 'text.disabled' : 'error.main' }} />
        <Typography sx={{ fontSize: '12px', color: granted ? 'text.secondary' : 'error.main' }}>
          {granted
            ? 'Retired model — this driver is grandfathered onto it'
            : 'This model is retired. The driver sees no trips until it is grandfathered or replaced.'}
        </Typography>
        {/* The line above is a FACT and renders without a handler; only the control
            needs one. Guarding the whole notice on `onChange` is what hid this from
            the user page for a release — the record is hosted in two places, one of
            them forgot to pass it, and the explanation went with the button. */}
        {onChange && (
          <Button
            size="small"
            disabled={busy}
            onClick={() => setAsking(true)}
            sx={{ textTransform: 'none', fontSize: '12px', minWidth: 0, py: 0 }}
          >
            {granted ? 'Revoke' : 'Grandfather this driver'}
          </Button>
        )}
      </Stack>

      {/* Confirmed in both directions, because both directions are somebody's week:
          granting waives a platform rule for one driver, revoking benches them the
          moment it lands. Neither is destructive, so the dialog explains rather than
          warns. */}
      <Dialog open={asking} onClose={() => setAsking(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
          {granted ? 'Revoke this exception?' : 'Grandfather this driver?'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
            {granted
              ? `${vehicle.label} goes back to being a retired model for this driver. Their trips disappear as soon as this lands.`
              : `${vehicle.label} stays retired for everyone else — nobody can add one, and this driver's insurance and registration still have to be valid. It only stops the model itself from benching them.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAsking(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={granted ? 'error' : 'primary'}
            disabled={busy}
            onClick={() => {
              setAsking(false);
              onChange(vehicle.driverVehicleId, !granted);
            }}
            sx={{ textTransform: 'none' }}
          >
            {granted ? 'Revoke exception' : 'Grandfather driver'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const OwnerCard = ({ owner, onSelect, busy, onRemoveVehicle, onGrandfatherVehicle }) => {
  /**
   * Background check is left out of this card. It has its own section below, which
   * carries the same status and its full report history — as a row here it was a
   * second place to read the same thing, and the only row that left the app.
   *
   * Out of the counts too, not just the rows: "1 blocking" over a card whose every
   * visible row is valid reads as a bug. The page-level count above still includes
   * it, and its own section still says whether it blocks.
   */
  const requirements = owner.requirements.filter((r) => r.id !== 'screening');
  const review = requirements.filter((r) => r.waitingOn === 'us').length;
  const blocking = requirements.filter((r) => r.blocking).length;
  // Everything else with something wrong with it — waiting on the driver, or not
  // verified yet. One bucket rather than two more tiers: the distinction is on the
  // rows, and what this line owes a reviewer is "not all valid, but not yours".
  const unresolved = requirements.filter((r) => r.remedy).length - review;
  const vehicle = owner.vehicle;

  // Our own queue first, because it is the only line of this that is a call to
  // action. "1 blocking" over a card whose one bad document is waiting on the
  // driver sent reviewers looking for something to do that was never theirs.
  const summary = review
    ? { label: `${review} to review`, color: 'accent.main' }
    : blocking
      ? { label: `${blocking} blocking`, color: 'error.main' }
      : unresolved
        ? { label: `${unresolved} unresolved`, color: 'warning.main' }
        : { label: 'all valid', color: 'text.disabled' };

  return (
    <SectionCard
      sx={{ mb: 2 }}
      title={owner.title}
      icon={<OwnerIcon kind={owner.kind} sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        <>
          {vehicle && <ItemStatusChip value={vehicle.cardStatus} />}
          {vehicle?.isDefault && (
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', whiteSpace: 'nowrap' }}>default</Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography
            sx={{
              fontSize: '11.5px',
              fontWeight: review ? 700 : 400,
              whiteSpace: 'nowrap',
              color: summary.color,
            }}
          >
            {summary.label}
          </Typography>
          {/* Last, and quiet: the destructive control on a card whose whole point is
              the requirement rows below it should not compete with them. */}
          {vehicle && onRemoveVehicle && <DeleteVehicle vehicle={vehicle} busy={busy} onConfirm={onRemoveVehicle} />}
        </>
      }
      band={
        vehicle && (
          <>
            <Stack direction="row" spacing={3} useFlexGap sx={{ px: 1.75, pb: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
              <Field label="Plate" value={vehicle.licensePlate} />
              <Field label="VIN" value={vehicle.vin} />
              <Field label="Color" value={vehicle.color} />
              <Field label="Seats" value={vehicle.seatCount} />
              {/* Derived server-side from the documents. This is the trustworthy one. */}
              <Field label="Ready to drive" value={vehicle.isReady ? 'Yes' : 'No'} />
              {/* `driver_vehicle.status` as STORED, shown so a drifted column is visible.
                  Every standing evaluation reconciles it to the documents, so it should
                  match the derived value the same response carries — a difference means
                  that write has not reached this row. Coloured only on that difference; a
                  bare value next to a matching one is noise. */}
              {vehicle.recordStatus && (
                <Box>
                  <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', mb: 0.25 }}>Record status</Typography>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: vehicle.statusDrifted ? 'error.main' : 'text.primary',
                    }}
                  >
                    {vehicle.recordStatus.toLowerCase().replace(/_/g, ' ')}
                    {vehicle.statusDrifted && ' — does not match documents'}
                  </Typography>
                </Box>
              )}
            </Stack>
            {/* Below the facts rather than among them: it explains the "Ready to
                drive" above it, and it carries a control, which a Field cannot. */}
            {(!vehicle.isEligible || vehicle.isGrandfathered) && (
              <VehicleEligibility vehicle={vehicle} busy={busy} onChange={onGrandfatherVehicle} />
            )}
          </>
        )
      }
    >
      {requirements.map((r) => (
        <Row key={r.id} item={r} ownerTitle={owner.title} onClick={() => onSelect(r.id)} />
      ))}
    </SectionCard>
  );
};

const RecordView = ({
  profile,
  mode,
  onModeChange,
  onSelect,
  checkrUrl,
  busy,
  onAddVehicle,
  onRemoveVehicle,
  onGrandfatherVehicle,
}) => {
  const total = profile.requirements.length;
  const review = profile.reviewQueue.length;
  // 'review' is our queue; 'actions' is that plus everything waiting on the driver.
  const rows = mode === 'review' ? profile.reviewQueue : profile.actions;

  // What is stuck, said only when it adds something. Independent of the count above:
  // a driver can be blocked with nothing for us to do, and the single line this
  // replaced could only ever report one of the two.
  const rest = profile.blockingCount
    ? `${profile.blockingCount} of ${total} blocking`
    : review
      ? null
      : profile.actions.length
        ? `nothing waiting on you · ${profile.actions.length} unresolved`
        : 'all requirements valid';

  return (
    <>
      {/* Wraps. Four things share this row — the label, the queue summary, Add vehicle,
          and a three-option mode control — which together want ~500px. Held on one line
          they overlapped into an unreadable pile on a phone; wrapping puts the label and
          summary on the first line and the two controls on the second. */}
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 1.5,
          rowGap: 1,
        }}
      >
        <SectionLabel>Requirements</SectionLabel>
        {/* Our own queue first and loudest — it is the only part of this a reviewer
            can act on. */}
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: '1 1 auto', minWidth: 0 }}>
          {review > 0 && (
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: 'accent.main', whiteSpace: 'nowrap' }}>
              {review} waiting on you
            </Typography>
          )}
          {rest && (
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              {review > 0 ? '· ' : ''}
              {rest}
            </Typography>
          )}
        </Stack>
        {/* Styled as a segment rather than as an outlined button: it shares a row with the
            mode control, and a bordered pill next to that soft track read as the louder of
            the two — which is backwards, since switching the view is the common action and
            adding a vehicle is the rare one. */}
        {/* The two controls travel together, so they wrap to the next line as a pair
            rather than splitting the mode control off on its own. */}
        {/* Full width once wrapped, so the mode control gets the whole line rather than
            whatever is left beside Add vehicle — three nowrap segments do not fit in the
            remainder. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flexShrink: 0, ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}
        >
          {onAddVehicle && (
            <SegmentedAction disabled={busy} onClick={onAddVehicle}>
              + Add vehicle
            </SegmentedAction>
          )}
          <SegmentedControl
            value={mode}
            onChange={onModeChange}
            options={[
              { key: 'all', label: 'All' },
              { key: 'review', label: 'Needs review' },
              { key: 'actions', label: 'All actions' },
            ]}
            sx={{ flex: { xs: 1, sm: 'none' }, minWidth: 0 }}
          />
        </Stack>
      </Box>

      {mode === 'all' ? (
        profile.owners.map((owner) => (
          <OwnerCard
            key={owner.key}
            owner={owner}
            onSelect={onSelect}
            busy={busy}
            onRemoveVehicle={onRemoveVehicle}
            onGrandfatherVehicle={onGrandfatherVehicle}
          />
        ))
      ) : rows.length ? (
        // Screening DOES appear here, unlike in the cards above: this mode is the
        // to-do list, and a check waiting on adjudication is work. Dropping it
        // would be the one action a reviewer never sees.
        <Box sx={{ border: (t) => `1px solid ${hairline(t)}`, borderRadius: '8px', overflow: 'hidden' }}>
          {rows.map((r) => {
            const owner = profile.owners.find((o) => o.key === r.group);
            return (
              <Row
                key={r.id}
                item={r}
                ownerTitle={owner ? owner.title : 'Driver'}
                onClick={() => onSelect(r.id)}
                external={r.id === 'screening' ? checkrUrl : null}
                imperative
              />
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{ border: (t) => `1px dashed ${hairline(t)}`, borderRadius: '8px', px: 2, py: 3, textAlign: 'center' }}
        >
          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
            {mode === 'review'
              ? profile.actions.length
                ? 'Nothing waiting on you. What is left is on the driver — see all actions.'
                : 'Nothing waiting on you. Every requirement on file is valid.'
              : 'Nothing to act on. Every requirement on file is valid.'}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default RecordView;
